import React from "react";
import "./CommunityPage.css";

const pages = {
  hse: {
    eyebrow: "HSE & COMMUNITY SAFETY",
    title: "Safe work. Stronger communities.",
    summary:
      "We bring health, safety and environmental responsibility together with meaningful community relationships, because progress should protect people and reach beyond the project.",
    image: "/images/Industrial Safety Briefing.png",
    imageAlt: "Offshore personnel taking part in a safety briefing",
    commitment: [
      {
        title: "Safety First",
        text: "Safety is our top priority, and we believe that every incident is preventable. We are dedicated to providing a safe and healthy work environment for all employees and contractors, implementing stringent safety protocols, and fostering a culture of safety awareness and accountability.",
      },
      {
        title: "Compliance and Regulation",
        text: "We adhere to all applicable laws, regulations, and industry standards related to health, safety, and environmental protection. We continuously monitor and update our practices to ensure compliance with evolving regulatory requirements and industry best practices.",
      },
      {
        title: "Risk Management",
        text: "We proactively identify, assess, and mitigate risks associated with our operations to prevent incidents and minimize their impact on people, property, and the environment. Our comprehensive risk management approach includes hazard identification, risk assessment, and implementation of controls to mitigate identified risks.",
      },
      {
        title: "Training and Competence",
        text: "We provide comprehensive training and development programs to ensure that our employees and contractors have the knowledge, skills, and competencies required to perform their roles safely and effectively. We invest in ongoing training to keep our workforce abreast of the latest HSE practices and technologies.",
      },
      {
        title: "Environmental Stewardship",
        text: "We are committed to minimizing our environmental footprint and promoting sustainable practices in all aspects of our operations. We strive to conserve natural resources, reduce waste, and mitigate the impacts of our activities on the environment through pollution prevention, energy efficiency, and responsible resource management.",
      },
      {
        title: "Continuous Improvement",
        text: "We believe in the importance of continuous improvement and strive to learn from our experiences, both positive and negative. We conduct regular reviews, audits, and assessments to identify areas for improvement and implement corrective actions to enhance our HSE performance.",
      },
    ],
    commitmentImage: "/images/CEOPortrait.webp",
    commitmentImageAlt: "Moritz A. Abazie",
    values: [
      {
        title: "Integrity",
        text: "We conduct our operations with honesty, transparency, and ethical integrity.",
      },
      {
        title: "Respect",
        text: "We respect the rights, dignity, and diversity of all individuals.",
      },
      {
        title: "Accountability",
        text: "We hold ourselves accountable for our actions and decisions, taking ownership of our responsibilities.",
      },
      {
        title: "Collaboration",
        text: "We work collaboratively with stakeholders to achieve common HSE goals and objectives.",
      },
      {
        title: "Excellence",
        text: "We strive for excellence in everything we do, aiming for the highest standards of HSE performance.",
      },
    ],
    awards: [
      {
        title: "Corporate Recognition Certificate",
        image: "/images/photos/awards/Corporate Recognition Certificate.webp",
      },
      {
        title: "Goal Zero Hero Award Certificate",
        image: "/images/photos/awards/Goal Zero Hero Award Certificate.webp",
      },
      {
        title: "OABP HSSE Award Plaque",
        image: "/images/photos/awards/OABP HSSE Award Plaque.webp",
      },
      {
        title: "West Asset HSE Award Plaque",
        image: "/images/photos/awards/West Asset HSE Award Plaque.webp",
      },
      {
        title: "Elegant HSSE Award Plaque",
        image: "/images/photos/awards/Elegant HSSE Award Plaque.webp",
      },
      {
        title: "Shell Safety Leadership Award Plaque",
        image: "/images/photos/awards/Shell Safety Leadership Award Plaque.webp",
      },
    ],
  },
  outreach: {
    eyebrow: "SERVICE & OUTREACH",
    title: "Showing up through service.",
    summary:
      "Responsible business includes time, care and practical support for people beyond our project sites. We believe useful service starts with understanding what is needed.",
    image: "/images/meeting.webp",
    imageAlt: "Strides colleagues meeting and planning together",
    introEyebrow: "PEOPLE FIRST",
    introTitle: "Service starts with people.",
    intro:
      "Our outreach is guided by partnership and practical action. We seek opportunities to contribute in ways that respond to real needs and reflect our commitment to responsible, connected operations.",
    focusEyebrow: "HOW WE SERVE",
    focusTitle: "Practical support, delivered together.",
    items: [
      {
        title: "Start with community voices",
        text: "Listening to local people helps shape outreach that is relevant, respectful and grounded in real priorities.",
      },
      {
        title: "Contribute in practical ways",
        text: "Time, knowledge and resources can make a difference when they respond to a clearly understood need.",
      },
      {
        title: "Bring people together",
        text: "Service is strongest when colleagues, partners and communities work toward a shared purpose.",
      },
    ],
  },
};

function CommunityPage({ page }) {
  const content = pages[page];

  return (
    <main className="community-page">
      <section className="community-hero">
        <img
          className="community-hero-image"
          src={content.image}
          alt={content.imageAlt}
        />
        <div className="community-hero-overlay" />
        <div className="community-container community-hero-content">
          <span className="community-eyebrow">{content.eyebrow}</span>
          <h1>{content.title}</h1>
          <p>{content.summary}</p>
        </div>
      </section>

      {!content.commitment && (
        <section className="community-intro">
          <div className="community-container community-intro-grid">
            <div>
              <span className="community-eyebrow">{content.introEyebrow}</span>
              <h2>{content.introTitle}</h2>
            </div>
            <p>{content.intro}</p>
          </div>
        </section>
      )}

      {content.communityIntro && (
        <section className="community-intro">
          <div className="community-container community-intro-grid">
            <div>
              <span className="community-eyebrow">{content.communityIntro.eyebrow}</span>
              <h2>{content.communityIntro.title}</h2>
            </div>
            <p>{content.communityIntro.text}</p>
          </div>
        </section>
      )}

      {content.commitment && (
        <section className="community-hse-commitment">
          <div className="community-container">
            <div className="community-focus-heading">
              <span className="community-eyebrow">HEALTH, SAFETY & ENVIRONMENT</span>
              <h2>Our HSE Commitment</h2>
            </div>
            <div className="community-hse-commitment-layout">
              <ol className="community-hse-commitment-list">
                {content.commitment.map((item) => (
                  <li key={item.title}>
                    <div>
                      <h3>{item.title}</h3>
                      <p>{item.text}</p>
                    </div>
                  </li>
                ))}
              </ol>
              <figure className="community-hse-portrait">
                <img
                  src={content.commitmentImage}
                  alt={content.commitmentImageAlt}
                  loading="lazy"
                />
              </figure>
            </div>
          </div>
        </section>
      )}

      {content.values && (
        <section className="community-hse-values">
          <div className="community-container">
            <div className="community-focus-heading">
              <span className="community-eyebrow">THE PRINCIPLES BEHIND OUR WORK</span>
              <h2>Our HSE Values</h2>
            </div>
            <div className="community-hse-values-grid">
              {content.values.map((value) => (
                <article key={value.title}>
                  <h3>{value.title}</h3>
                  <p>{value.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {content.items && (
        <section className="community-focus">
          <div className="community-container">
            <div className="community-focus-heading">
              <span className="community-eyebrow">{content.focusEyebrow}</span>
              <h2>{content.focusTitle}</h2>
            </div>
            <div className="community-focus-grid">
              {content.items.map((item) => (
                <article className="community-focus-item" key={item.title}>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {content.awards && (
        <section className="community-awards">
          <div className="community-container">
            <div className="community-focus-heading">
              <span className="community-eyebrow">RECOGNITION</span>
              <h2>Awards and recognition</h2>
            </div>
            <div className="community-awards-grid">
              {content.awards.map((award) => (
                <figure className="community-award" key={award.title}>
                  <img src={award.image} alt={award.title} loading="lazy" />
                  <figcaption>{award.title}</figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="community-contact">
        <div className="community-container community-contact-inner">
          <div>
            <span className="community-eyebrow">WORKING TOGETHER</span>
            <h2>Let’s make a positive difference.</h2>
          </div>
          <a href="/contact" className="community-contact-link">
            Contact our team <span aria-hidden="true">→</span>
          </a>
        </div>
      </section>
    </main>
  );
}

export default CommunityPage;